import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiErrorDetail, ROLE_HOME } from "../lib/api";
import { BrandMark } from "./AuthLayout";
import { useAuth } from "../context/AuthContext";
import { useOnline } from "../components/OfflineBanner";
import { useAppT, LanguageToggle } from "../components/home/i18n";
import { CATEGORIES } from "../config/brand";
import { MessageSquare, Flag, Pin, ShieldCheck, Plus, Send, X, Image as ImageIcon, Film, Eye } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (id) => `${BACKEND}/api/community/media/${id}/file`;
const ext = (n) => (n?.includes(".") ? n.split(".").pop().toLowerCase() : "");
const getDuration = (file) => new Promise((res) => {
  try { const v = document.createElement("video"); v.preload = "metadata"; v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); res(v.duration || null); }; v.onerror = () => res(null); v.src = URL.createObjectURL(file); }
  catch { res(null); }
});

const CAT_TONE = {
  "Incidents & Safety": "bg-[#8B3A3A]/10 text-[#8B3A3A] border-[#8B3A3A]/25",
  Jobs: "bg-[#1F4A38]/10 text-[#1F4A38] border-[#1F4A38]/25",
};
const catTone = (c) => CAT_TONE[c] || "bg-[#C4A46B]/15 text-[#8a6f38] border-[#C4A46B]/40";

function MediaGrid({ media, a }) {
  if (!media?.length) return null;
  return (
    <div className={`mt-3 grid gap-2 ${media.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
      {media.map((m) => (
        <div key={m.id} className="overflow-hidden rounded-md border border-[#E8E4DB] bg-[#F3F0E9]">
          {m.review_status !== "approved"
            ? <div data-testid={`media-pending-${m.id}`} className="flex h-48 w-full flex-col items-center justify-center gap-1 text-center text-[#8a8580]"><Eye className="h-5 w-5" /><span className="text-xs font-medium">{a.community.awaitingReview}</span><span className="text-[10px]">{a.community.awaitingReviewSub}</span></div>
            : m.kind === "post_video"
              ? <video data-testid={`post-video-${m.id}`} src={mediaUrl(m.id)} controls preload="metadata" className="h-48 w-full bg-black object-cover" />
              : <img data-testid={`post-image-${m.id}`} src={mediaUrl(m.id)} alt="" loading="lazy" className="h-48 w-full object-cover" />}
        </div>
      ))}
    </div>
  );
}

function AuthorLine({ author, a }) {
  const label = author?.role === "candidate" ? (author.alias || a.community.candidate)
    : author?.name || a.community.member;
  const role = author?.role ? (a.respMap[author.role] || author.role) : "";
  return (
    <div className="flex items-center gap-2 text-xs text-[#5C5855]">
      <span className="font-semibold text-[#2C2A29]">{label}</span>
      {role && <span className="rounded-full bg-[#1F4A38]/8 px-2 py-0.5 text-[10px] font-semibold text-[#1F4A38]">{role}</span>}
    </div>
  );
}

function Comments({ postId, canPost, online, a }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const load = async () => { const { data } = await API.get(`/community/posts/${postId}`); setComments(data.comments || []); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  const submit = async () => {
    if (!text.trim()) return;
    try { await API.post(`/community/posts/${postId}/comments`, { body: text }); setText(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const report = async (cid) => {
    try { await API.post(`/community/comments/${cid}/report`, { reason: "Reported from feed" }); toast.success(a.community.reported); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 border-t border-[#E8E4DB] pt-3" data-testid={`comments-${postId}`}>
      {comments === null ? <div className="text-xs text-[#8a8580]">{a.community.loadingComments}</div>
        : comments.length === 0 ? <div className="text-xs text-[#8a8580]">{a.community.noComments}</div>
        : comments.map((c) => (
          <div key={c.id} className="mb-2 rounded-sm bg-[#FDFBF7] p-2">
            <div className="flex items-center justify-between"><AuthorLine author={c.author} a={a} />
              {canPost && <button data-testid={`report-comment-${c.id}`} onClick={() => report(c.id)} className="text-[10px] text-[#8B3A3A]"><Flag className="inline h-3 w-3" /> {a.community.report}</button>}
            </div>
            <div className="mt-1 text-sm text-[#2C2A29]">{c.body}</div>
          </div>
        ))}
      {canPost && (
        <div className="mt-2 flex gap-2">
          <input data-testid={`comment-input-${postId}`} value={text} onChange={(e) => setText(e.target.value)} placeholder={a.community.addComment}
            className="flex-1 rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]" onKeyDown={(e) => e.key === "Enter" && online && submit()} />
          <button data-testid={`comment-submit-${postId}`} disabled={!online} onClick={submit} className="rounded-sm bg-[#1F4A38] px-3 text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

function PostCard({ post, canPost, online, a }) {
  const [open, setOpen] = useState(false);
  const report = async () => {
    try { await API.post(`/community/posts/${post.id}/report`, { reason: "Reported from feed" }); toast.success(a.community.reported); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <article data-testid={`post-${post.id}`} className={`rounded-md border bg-white p-5 ${post.pinned ? "border-[#C4A46B]" : "border-[#E8E4DB]"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${catTone(post.category)}`}>{a.catMap[post.category] || post.category}</span>
        {post.pinned && <span data-testid={`pinned-${post.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#C4A46B]/20 px-2 py-0.5 text-[10px] font-semibold text-[#8a6f38]"><Pin className="h-3 w-3" /> {a.community.pinned}</span>}
        {post.verified_resource && <span className="inline-flex items-center gap-1 rounded-full bg-[#2E6B4B]/12 px-2 py-0.5 text-[10px] font-semibold text-[#2E6B4B]"><ShieldCheck className="h-3 w-3" /> {a.community.verifiedResource}</span>}
        <span className="ml-auto text-[11px] text-[#8a8580]">{post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}</span>
      </div>
      <h3 className="mt-2 font-serif text-xl text-[#173A2C]">{post.title}</h3>
      {post.body && <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#2C2A29]">{post.body}</p>}
      <MediaGrid media={post.media} a={a} />
      <div className="mt-3 flex items-center justify-between">
        <AuthorLine author={post.author} a={a} />
        <div className="flex items-center gap-3 text-xs">
          <button data-testid={`toggle-comments-${post.id}`} onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-[#1F4A38]"><MessageSquare className="h-3.5 w-3.5" /> {post.comment_count} {post.comment_count === 1 ? a.community.commentOne : a.community.commentOther}</button>
          {canPost && <button data-testid={`report-post-${post.id}`} onClick={report} className="inline-flex items-center gap-1 text-[#8B3A3A]"><Flag className="h-3.5 w-3.5" /> {a.community.report}</button>}
        </div>
      </div>
      {open && <Comments postId={post.id} canPost={canPost} online={online} a={a} />}
    </article>
  );
}

function Composer({ onClose, onDone, online, a }) {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    const chosen = Array.from(e.target.files || []).slice(0, 4 - files.length);
    setFiles((f) => [...f, ...chosen]);
    e.target.value = "";
  };

  const submit = async () => {
    if (!title.trim()) { toast.error(a.community.titleRequired); return; }
    setBusy(true);
    try {
      const { data: post } = await API.post("/community/posts", { category, title, body });
      for (const file of files) {
        const e = ext(file.name);
        const isVideo = ["mp4", "mov"].includes(e);
        const fd = new FormData();
        fd.append("kind", isVideo ? "post_video" : "post_image");
        if (isVideo) { const d = await getDuration(file); if (d) fd.append("duration_s", Math.round(d)); }
        fd.append("file", file);
        await API.post(`/community/posts/${post.id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(a.community.posted);
      onDone();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="composer-modal">
      <div className="w-full max-w-lg rounded-md bg-white p-5">
        <div className="flex items-center justify-between"><h2 className="font-serif text-xl text-[#173A2C]">{a.community.shareTitle}</h2><button onClick={onClose} data-testid="composer-close"><X className="h-5 w-5 text-[#5C5855]" /></button></div>
        <div className="mt-4 space-y-3">
          <select data-testid="composer-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]">
            {CATEGORIES.map((c) => <option key={c} value={c}>{a.catMap[c] || c}</option>)}
          </select>
          <input data-testid="composer-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={a.community.titlePlaceholder} className="w-full rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]" />
          <textarea data-testid="composer-body" rows="4" value={body} onChange={(e) => setBody(e.target.value)} placeholder={a.community.bodyPlaceholder} className="w-full rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]" />
          <div className="flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-[#E8E4DB] px-3 py-1.5 text-xs text-[#1F4A38]"><ImageIcon className="h-3.5 w-3.5" /><Film className="h-3.5 w-3.5" /> {a.community.addMedia}
              <input data-testid="composer-file" type="file" accept=".jpg,.jpeg,.png,.webp,.mp4,.mov" multiple className="hidden" onChange={pick} disabled={files.length >= 4} />
            </label>
            <span className="text-[11px] text-[#8a8580]">{files.length}/4 {a.community.mediaHint}</span>
          </div>
          {files.length > 0 && <div className="text-xs text-[#5C5855]">{files.map((f) => f.name).join(", ")}</div>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-[#E8E4DB] px-4 py-2 text-sm">{a.common.cancel}</button>
          <button data-testid="composer-submit" disabled={busy || !online} onClick={submit} className="rounded-full bg-[#1F4A38] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? a.community.posting : a.community.post}</button>
        </div>
      </div>
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const { a } = useAppT();
  const navigate = useNavigate();
  const online = useOnline();
  const [active, setActive] = useState("All");
  const [posts, setPosts] = useState([]);
  const [showComposer, setShowComposer] = useState(false);
  const canPost = !!user;

  const load = async () => {
    const { data } = await API.get("/community/posts", { params: active === "All" ? {} : { category: active } });
    setPosts(data.posts);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);
  const cats = useMemo(() => ["All", ...CATEGORIES], []);
  const catLabel = (c) => c === "All" ? a.community.all : (a.catMap[c] || c);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E8E4DB] bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-2 px-5 py-3">
          <BrandMark />
          <nav className="flex items-center gap-2">
            <LanguageToggle testid="community-lang-toggle" variant="dark" />
            {user ? <Link to={ROLE_HOME[user.role]} data-testid="community-dashboard-link" className="rounded-full px-4 py-2 text-sm text-[#1F4A38]">{a.community.myDashboard}</Link>
              : <>
                <Link to="/login" className="rounded-full px-4 py-2 text-sm text-[#5C5855]">{a.community.signin}</Link>
                <Link to="/register" className="rounded-full bg-[#C4A46B] px-4 py-2 text-sm font-semibold text-[#173A2C]">{a.community.join}</Link>
              </>}
          </nav>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-5 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-serif text-4xl text-[#173A2C]">{a.community.title}</h1>
            <p className="mt-1 text-sm text-[#5C5855]">{a.community.sub}</p>
          </div>
          {canPost
            ? <button data-testid="new-post-btn" disabled={!online} onClick={() => setShowComposer(true)} className="inline-flex items-center gap-1 rounded-full bg-[#1F4A38] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Plus className="h-4 w-4" /> {a.community.newPost}</button>
            : <button data-testid="signin-to-post-btn" onClick={() => navigate("/login")} className="rounded-full border border-[#1F4A38] px-5 py-2.5 text-sm font-semibold text-[#1F4A38]">{a.community.signinToPost}</button>}
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="category-filters">
          {cats.map((c) => (
            <button key={c} data-testid={`cat-${c}`} onClick={() => setActive(c)}
              className={`rounded-full border px-3 py-1 text-sm ${active === c ? "border-[#1F4A38] bg-[#1F4A38] text-white" : "border-[#E8E4DB] bg-white text-[#5C5855] hover:text-[#2C2A29]"}`}>{catLabel(c)}</button>
          ))}
        </div>

        <div className="mt-6 space-y-4" data-testid="community-feed">
          {posts.length === 0 && <div className="rounded-md border border-dashed border-[#E8E4DB] bg-white p-8 text-center text-sm text-[#5C5855]">{canPost ? a.community.emptyCanPost : a.community.emptyGuest}</div>}
          {posts.map((p) => <PostCard key={p.id} post={p} canPost={canPost} online={online} a={a} />)}
        </div>
      </div>

      {showComposer && <Composer onClose={() => setShowComposer(false)} onDone={load} online={online} a={a} />}
    </div>
  );
}
